import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Save, Image as ImageIcon, X, Plus, ArrowUp, ArrowDown, Trash2, FileText, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Toaster, toast } from "sonner";
import MediaPicker from "@/admin/components/MediaPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLUG = "why";

// Reusable section card wrapper for the editor.
function SectionCard({ title, description, children }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5 lg:p-6">
      <header className="mb-5">
        <h2 className="text-sm font-bold text-slate-900">{title}</h2>
        {description && <p className="text-xs text-slate-500 mt-1">{description}</p>}
      </header>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-700">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

export default function AdminWhyPageEditor() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/pages/built-in/${SLUG}`);
        setContent({
          ...data,
          // Ensure array is always present so the pillar editor doesn't crash.
          story_pillars: Array.isArray(data.story_pillars) ? data.story_pillars : [],
        });
      } catch {
        toast.error("Could not load /why content.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setField = (key) => (e) => {
    const val = e?.target ? e.target.value : e;
    setContent((prev) => ({ ...prev, [key]: val }));
  };

  const setPillarField = (idx, key) => (e) => {
    const val = e?.target ? e.target.value : e;
    setContent((prev) => {
      const next = [...(prev.story_pillars || [])];
      next[idx] = { ...next[idx], [key]: val };
      return { ...prev, story_pillars: next };
    });
  };

  const addPillar = () =>
    setContent((prev) => ({
      ...prev,
      story_pillars: [...(prev.story_pillars || []), { title: "", body: "" }],
    }));

  const removePillar = (idx) => {
    if (!window.confirm("Remove this pillar?")) return;
    setContent((prev) => ({
      ...prev,
      story_pillars: (prev.story_pillars || []).filter((_, i) => i !== idx),
    }));
  };

  const movePillar = (idx, direction) => {
    setContent((prev) => {
      const arr = [...(prev.story_pillars || [])];
      const next = direction === "up" ? idx - 1 : idx + 1;
      if (next < 0 || next >= arr.length) return prev;
      [arr[idx], arr[next]] = [arr[next], arr[idx]];
      return { ...prev, story_pillars: arr };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pages/built-in/${SLUG}`, content);
      toast.success("Saved. Changes are live on /why.");
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !content) {
    return (
      <div className="p-8 text-sm text-slate-400" data-testid="admin-why-loading">
        Loading…
      </div>
    );
  }

  return (
    <div data-testid="admin-why-editor" className="bg-slate-50 min-h-full">
      <Toaster position="top-right" richColors />

      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 lg:px-8 py-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-slate-900 min-w-0">
          <FileText className="h-4 w-4 text-sky-600 shrink-0" />
          <h1 className="text-base font-bold truncate">Why Antera page</h1>
          <span className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">/why</span>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="/why"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="admin-why-preview"
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 px-2"
          >
            <ExternalLink className="h-3.5 w-3.5" /> View live
          </a>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="admin-why-save"
            className="bg-sky-600 hover:bg-sky-700 text-white h-9 rounded-lg text-xs font-semibold"
          >
            <Save className="h-3.5 w-3.5 mr-1" />
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>

      <div className="p-5 lg:p-8 space-y-5 max-w-4xl">
        <SectionCard
          title="1. Hero"
          description="Top of the page. The accent word appears in sky blue."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Title (main text)">
              <Input
                value={content.hero_title_prefix || ""}
                onChange={setField("hero_title_prefix")}
                data-testid="admin-why-hero-title-prefix"
              />
            </Field>
            <Field label="Title accent (highlighted word)">
              <Input
                value={content.hero_title_accent || ""}
                onChange={setField("hero_title_accent")}
                data-testid="admin-why-hero-title-accent"
              />
            </Field>
          </div>
          <Field label="Hero image">
            <div className="mt-1 flex items-start gap-4">
              {content.hero_image ? (
                <img
                  src={content.hero_image.startsWith("http") ? content.hero_image : `${process.env.REACT_APP_BACKEND_URL}${content.hero_image}`}
                  alt=""
                  className="h-24 w-40 rounded-lg object-cover border border-slate-200"
                  data-testid="admin-why-hero-preview"
                />
              ) : (
                <div className="h-24 w-40 rounded-lg bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center text-slate-400">
                  <ImageIcon className="h-6 w-6" />
                </div>
              )}
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPickerOpen(true)}
                  data-testid="admin-why-hero-pick"
                  className="h-8 text-xs"
                >
                  <ImageIcon className="h-3.5 w-3.5 mr-1" />
                  {content.hero_image ? "Replace image" : "Choose image"}
                </Button>
                {content.hero_image && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setField("hero_image")("")}
                    className="h-8 text-xs text-red-500 hover:text-red-600 hover:bg-red-50 justify-start"
                  >
                    <X className="h-3.5 w-3.5 mr-1" />
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </Field>
          <Field label="Hero image alt text" hint="Shown to screen readers and if the image fails to load.">
            <Input
              value={content.hero_image_alt || ""}
              onChange={setField("hero_image_alt")}
              data-testid="admin-why-hero-alt"
            />
          </Field>
        </SectionCard>

        <SectionCard
          title="2. Innovation Rooted in Partnership"
          description="Main story section — heading, intro paragraph, plus the four pillars underneath."
        >
          <Field label="Section heading">
            <Input
              value={content.story_heading || ""}
              onChange={setField("story_heading")}
              data-testid="admin-why-story-heading"
            />
          </Field>
          <Field label="Intro paragraph">
            <Textarea
              value={content.story_intro || ""}
              onChange={setField("story_intro")}
              className="min-h-[110px]"
              data-testid="admin-why-story-intro"
            />
          </Field>

          <div>
            <Label className="text-xs font-semibold text-slate-700">Pillars</Label>
            <div className="mt-2 space-y-3">
              {(content.story_pillars || []).map((p, idx) => (
                <div
                  key={idx}
                  className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50/40"
                  data-testid={`admin-why-pillar-${idx}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase font-bold tracking-widest text-slate-400">
                      Pillar {idx + 1}
                    </span>
                    <div className="flex items-center gap-0.5">
                      <button
                        type="button"
                        onClick={() => movePillar(idx, "up")}
                        disabled={idx === 0}
                        data-testid={`admin-why-pillar-up-${idx}`}
                        className="p-1 rounded text-slate-400 hover:bg-slate-200 disabled:opacity-30"
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => movePillar(idx, "down")}
                        disabled={idx === (content.story_pillars || []).length - 1}
                        data-testid={`admin-why-pillar-down-${idx}`}
                        className="p-1 rounded text-slate-400 hover:bg-slate-200 disabled:opacity-30"
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removePillar(idx)}
                        data-testid={`admin-why-pillar-remove-${idx}`}
                        className="p-1 rounded text-slate-400 hover:bg-red-50 hover:text-red-500"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <Input
                    value={p.title || ""}
                    onChange={setPillarField(idx, "title")}
                    placeholder="Pillar title"
                    className="text-sm"
                    data-testid={`admin-why-pillar-title-${idx}`}
                  />
                  <Textarea
                    value={p.body || ""}
                    onChange={setPillarField(idx, "body")}
                    placeholder="Pillar body"
                    className="min-h-[90px] text-sm"
                    data-testid={`admin-why-pillar-body-${idx}`}
                  />
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addPillar}
              className="h-8 text-xs mt-3"
              data-testid="admin-why-pillar-add"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Add pillar
            </Button>
          </div>
        </SectionCard>

        <SectionCard
          title="3. Mission & Vision"
          description="Two cards side-by-side under the story section."
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <Field label="Mission heading">
                <Input
                  value={content.mission_heading || ""}
                  onChange={setField("mission_heading")}
                  data-testid="admin-why-mission-heading"
                />
              </Field>
              <Field label="Mission body">
                <Textarea
                  value={content.mission_body || ""}
                  onChange={setField("mission_body")}
                  className="min-h-[160px]"
                  data-testid="admin-why-mission-body"
                />
              </Field>
            </div>
            <div className="space-y-3">
              <Field label="Vision heading">
                <Input
                  value={content.vision_heading || ""}
                  onChange={setField("vision_heading")}
                  data-testid="admin-why-vision-heading"
                />
              </Field>
              <Field label="Vision body">
                <Textarea
                  value={content.vision_body || ""}
                  onChange={setField("vision_body")}
                  className="min-h-[160px]"
                  data-testid="admin-why-vision-body"
                />
              </Field>
            </div>
          </div>
        </SectionCard>

        <SectionCard
          title="4. Team heading"
          description="Team members themselves live under Team — this only edits the heading above the cards."
        >
          <Field label="Team section heading">
            <Input
              value={content.team_heading || ""}
              onChange={setField("team_heading")}
              data-testid="admin-why-team-heading"
            />
          </Field>
          <Link
            to="/admin/team"
            className="inline-flex items-center gap-1 text-xs font-semibold text-sky-600 hover:text-sky-700"
          >
            → Edit team members
          </Link>
        </SectionCard>

        <SectionCard
          title="5. Bottom CTA banner"
          description="The sky-blue call-to-action at the very bottom of the page."
        >
          <Field label="Heading">
            <Input
              value={content.cta_heading || ""}
              onChange={setField("cta_heading")}
              data-testid="admin-why-cta-heading"
            />
          </Field>
          <Field label="Body">
            <Textarea
              value={content.cta_body || ""}
              onChange={setField("cta_body")}
              className="min-h-[80px]"
              data-testid="admin-why-cta-body"
            />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Primary button label">
              <Input
                value={content.cta_primary_label || ""}
                onChange={setField("cta_primary_label")}
                data-testid="admin-why-cta-primary-label"
              />
            </Field>
            <Field label="Primary button link">
              <Input
                value={content.cta_primary_href || ""}
                onChange={setField("cta_primary_href")}
                data-testid="admin-why-cta-primary-href"
                placeholder="/contact"
              />
            </Field>
            <Field label="Secondary button label">
              <Input
                value={content.cta_secondary_label || ""}
                onChange={setField("cta_secondary_label")}
                data-testid="admin-why-cta-secondary-label"
              />
            </Field>
            <Field label="Secondary button link">
              <Input
                value={content.cta_secondary_href || ""}
                onChange={setField("cta_secondary_href")}
                data-testid="admin-why-cta-secondary-href"
              />
            </Field>
          </div>
        </SectionCard>

        <SectionCard
          title="6. SEO metadata"
          description="Search-engine title, description, and keywords for /why."
        >
          <Field label="SEO title">
            <Input
              value={content.seo_title || ""}
              onChange={setField("seo_title")}
              data-testid="admin-why-seo-title"
            />
          </Field>
          <Field label="SEO description">
            <Textarea
              value={content.seo_description || ""}
              onChange={setField("seo_description")}
              className="min-h-[70px]"
              data-testid="admin-why-seo-description"
            />
          </Field>
          <Field label="SEO keywords" hint="Comma-separated.">
            <Input
              value={content.seo_keywords || ""}
              onChange={setField("seo_keywords")}
              data-testid="admin-why-seo-keywords"
            />
          </Field>
        </SectionCard>
      </div>

      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(url) => {
          setField("hero_image")(url);
          setPickerOpen(false);
        }}
      />
    </div>
  );
}
